import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CalendarCheck, Coins, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FxRevaluationTab } from '@/components/accounting/period-end/fx-revaluation-tab';
import { YearEndCloseTab } from '@/components/accounting/period-end/year-end-close-tab';
import type { TieOutApp } from '@/components/accounting/tie-out/model';

type TabId = 'fx' | 'close';
const TABS: Array<{ id: TabId; icon: typeof Coins }> = [
  { id: 'fx', icon: Coins },
  { id: 'close', icon: Lock },
];

interface PeriodEndPageProps {
  app: TieOutApp;
}

/**
 * Dönem Sonu İşlemleri — the accountant's period-end desk: FX revaluation of
 * foreign-currency balances (preview, post, auto-reverse, void) and the
 * year-end close (cost reflection, P&L → 690 → 691/692 → 590/591, opening
 * transfer), each previewed line by line before anything is posted.
 */
export function PeriodEndPage({ app }: PeriodEndPageProps) {
  const { t } = useTranslation('periodEnd');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = TABS.some((tab) => tab.id === tabParam) ? (tabParam as TabId) : 'fx';
  const hubHref = app === 'TRAVEL' ? '/travel/accounting' : '/trading/accounting';

  return (
    <div className="space-y-6">
      <div>
        <Link to={hubHref} className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" />
          {t('backToAccounting')}
        </Link>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <CalendarCheck className="h-6 w-6" />
          {t('title')}
        </h1>
        <p className="text-muted-foreground">{t(app === 'TRAVEL' ? 'subtitleTravel' : 'subtitleTrading')}</p>
      </div>

      <div className="border-b border-border">
        <nav className="flex gap-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() =>
                setSearchParams(
                  (prev) => {
                    const params = new URLSearchParams(prev);
                    if (tab.id === 'fx') params.delete('tab');
                    else params.set('tab', tab.id);
                    return params;
                  },
                  { replace: true }
                )
              }
              className={cn(
                'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
                activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {t(`tabs.${tab.id}`)}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'fx' && <FxRevaluationTab app={app} />}
      {activeTab === 'close' && <YearEndCloseTab app={app} />}
    </div>
  );
}
